// ============================================================
// notificationFeatures.js — drives the Admin Console's "Notification
// Recipients" page, deliberately SEPARATE from permissionConfig.js's
// DOMAINS. That array is about page-permission gating (Roles &
// Permissions); this one is about "who gets emailed for what alert."
// Conflating the two is what produced the short-lived 'so_alerts' domain
// in permissionConfig.js (a fake page-permission domain with no
// `functions`, added purely to get a second recipient list) — this file
// exists so a future one-off alert type never needs that workaround again:
// just add another entry to some domain's `features` array here.
//
// Each domain's `features` array lists its independently-recipient-scoped
// alert types. `kind: 'event'` alerts are instant, sent as they happen —
// no schedule to configure. `kind: 'report'` alerts run on an admin-chosen
// cadence (see notification-schedules.js) with a sensible default if never
// configured. `comingSoon: true` marks a feature with no backend wired up
// yet — recipients and schedule are still fully configurable, ready for
// whenever it's built (see backend/src/functions/scheduled-reports-timer.js's
// REPORT_BUILDERS for report-kind features once implemented).
//
// A domain with a single `{ key: null, label: 'General', kind: 'event' }`
// feature hasn't been broken into named alert types yet — it just renders
// its one flat recipient list exactly as every domain did before this
// feature/schedule concept existed.
// ============================================================

export const NOTIFICATION_DOMAINS = [
    {
        key: 'grn_management', label: 'GRN Management', color: '#4A6FA5', tint: '#E5EEF7',
        features: [
            { key: 'grn_daily_summary',     label: 'Daily Summary Report',  kind: 'report' },
            { key: 'grn_discrepancy_alert', label: 'GRN Discrepancy Alert', kind: 'event' },
        ],
    },
    {
        key: 'po_so_lifecycle', label: 'PO & SO Lifecycle', color: '#4A6FA5', tint: '#E5EEF7',
        features: [
            { key: 'po_received',         label: "PO's Received",       kind: 'event' },
            { key: 'po_summary_report',   label: 'PO Summary Report',   kind: 'report' },
            // perKam: each recipient can be tagged to one KAM (gets only that KAM's
            // parties' SOs); untagged recipients get every alert. See
            // backend/shared/kamReviewAlert.js.
            { key: 'so_kam_review_alert', label: 'SO KAM Review Alert', kind: 'event', perKam: true },
            { key: 'so_pending_alert',    label: 'SO Pending Alert (24hr)', kind: 'report' },
        ],
    },
    {
        // RTV Record (PO & SO Lifecycle). Only Dispose Alert is wired
        // (backend/shared/rtvAlerts.js); the rest have recipient lists ready,
        // their trigger rules are still to be specified.
        key: 'rtv_record', label: 'RTV Record', color: '#B0553F', tint: '#F5E4DC',
        features: [
            { key: 'dispose_alert',        label: 'Dispose Alert',        kind: 'event' },
            { key: 'kam_approval_pending', label: 'KAM Approval Pending', kind: 'event', comingSoon: true },
            { key: 'credit_note_alert',    label: 'Credit Note Alert',    kind: 'event', comingSoon: true },
            { key: 'pickup_schedule',      label: 'Pickup Schedule',      kind: 'event', comingSoon: true },
            { key: 'schemes_and_offers',   label: 'Schemes and Offers',   kind: 'event', comingSoon: true },
            { key: 'rtv_alert',            label: 'RTV Alert',            kind: 'event', comingSoon: true },
        ],
    },
    {
        key: 'invoicing', label: 'Invoicing', color: '#2F8F82', tint: '#E3F2F0',
        features: [
            { key: 'so_alerts',      label: 'SO Alerts',      kind: 'event' },
            { key: 'invoice_alerts', label: 'Invoice Alerts', kind: 'event' },
        ],
    },
    {
        key: 'inventory', label: 'Inventory', color: '#5C7E60', tint: '#E7F0E8',
        features: [
            { key: 'daily_stock_report', label: 'Daily Stock Report', kind: 'report', comingSoon: true },
            { key: 'changed_sku_report', label: 'Changed SKU Report', kind: 'report', comingSoon: true },
            { key: 'new_product_launch', label: 'New Product Launch', kind: 'event',  comingSoon: true },
        ],
    },
    // Not yet broken into named features — single flat "General" list,
    // matching every domain's behavior before this file existed.
    { key: 'sales',       label: 'Sales',      color: '#6E58A6', tint: '#EEE9F8', features: [{ key: null, label: 'General', kind: 'event' }] },
    { key: 'dms',         label: 'DMS',        color: '#9A7B3E', tint: '#F5EDDC', features: [{ key: null, label: 'General', kind: 'event' }] },
    { key: 'platform',    label: 'Platform',   color: '#B05770', tint: '#F7E5EA', features: [{ key: null, label: 'General', kind: 'event' }] },
    { key: 'warehouse',   label: 'Warehouse',  color: '#3A3A3A', tint: '#F1EEF6', features: [{ key: null, label: 'General', kind: 'event' }] },
    { key: 'pod_upload',  label: 'POD Upload', color: '#3A3A3A', tint: '#F1EEF6', features: [{ key: null, label: 'General', kind: 'event' }] },
    { key: 'd2c',         label: 'D2C',        color: '#B0553F', tint: '#F5E4DC', features: [{ key: null, label: 'General', kind: 'event' }] },
];

export const FREQUENCIES = [
    { key: 'daily',   label: 'Daily' },
    { key: 'weekly',  label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
];

export const DAYS_OF_WEEK = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
];
